import { IdConta, IdContato, IdInbox, IdUsuario } from "../common-types";
import { GenericService } from "../generic-service";
import { StorageService } from "../../stores/admin/storage";
import { ResponseUsuario } from "../usuarios/usuarios.service";
import type { RespostaFotoDeContato } from '@/utils/fotoDeContato';

export type CreateContato = {
  nome: string,
  codigo: string,
  observacao?: string,
  imagem?: string | null,
  tipo: string,
  idConta: IdConta,
  agenteIaAtivado?: boolean,
}

export type UpdateContato = Partial<CreateContato> & { id: IdContato }

export type ResponseContato = {
  id: IdContato;
  nome: string,
  nomeWhatsapp: string,
  codigo: string,
  observacao?: string,
  imagem?: string,
  tipo: string,
  idConta: IdConta,
  agenteIaAtivado?: boolean,
  idClienteAxion?: string,
  nomeClienteAxion?: string,
  idResponsavelAxion?: string,
  updatedAt?: string,
  etiquetas: { id: string; nome: string; cor: string; descricao?: string }[],
}

export type ShortContato = {
  nome: string,
  codigo: string,
}

export type ContatoSemAtendimento = {
  id: IdContato,
  nome: string,
  codigo: string,
  imagem: string | null,
  etiquetas: { id: string; nome: string; cor: string }[],
}

export type SincronizarContatosDto = {
  idInbox: string;
  sobreescreverDados: boolean;
  sincronizarImagens?: boolean;
}

export type ImportarContatosDto = {
  idConta: string;
  sobreescreverDados: boolean;
  file: File;
}

export type FiltroContatoImportacaoDto = {
  idConta: string;
}

export type FiltroContatoSincronizacaoDto = {
  idConta: string;
}

export type ResponseContatoImportacaoDto = {
  id: string;
  nomeArquivo?: string;
  url: string;
  sobreescreverDados: boolean;
  dataCriacao: Date;
  dataInicioProcessamento?: Date;
  dataTerminoProcessamento?: Date;
  linhasJaProcessadas: number;
  totalLinhas?: number;
  status: string;
  idConta: string;
  idUsuario?: string;
  dataConfirmacao?: Date | null;
  dataAnaliseSolicitada?: Date | null;
  qtdErros?: number;
}

export type ResponseContatoImportacaoLogDto = {
  id: number;
  linha: number;
  dados: ResponseContatoImportacaoLogDadosDto;
  status: string;
  erro?: string;
};

export type ResponseContatoImportacaoLogDadosDto = {
  nome: string; numero: string;
}

// Conferência da importação (task 104807).
export type FiltroIndicadorImportacao = 'SUCESSO' | 'ERRO' | 'BRASIL' | 'ESTRANGEIRO';

export type PaginacaoImportacaoDto = {
  filtro?: FiltroIndicadorImportacao;
  pagina?: number;
  tamanho?: number;
}

export type IndicadoresImportacaoDto = {
  total: number;
  sucesso: number;
  erro: number;
  brasil: number;
  estrangeiro: number;
}

export type LinhaConferenciaDto = {
  id: string;
  linha: number;
  nome: string | null;
  ddi: string | null;
  numeroOriginal: string | null;
  numero: string | null;
  etiquetas: string | null;
  codigo: string | null;
  pais: string | null;
  paisNome: string | null;
  status: 'VALIDA' | 'ERRO';
  motivo: string | null;
  erro: string | null;
  sugestao: string | null;
  editada: boolean;
}

export type ResultadoLinhaDto = {
  id: string;
  linha: number;
  status: string;
  erro: string | null;
  nome: string | null;
  numero: string | null;
  etiquetas: string[];
  pais: string | null;
  paisNome: string | null;
}

export type PaginaImportacaoDto<T> = {
  indicadores: IndicadoresImportacaoDto;
  itens: T[];
  total: number;
  pagina: number;
  tamanho: number;
  /** Só na conferência: há linhas depois desta página (cursor). */
  temProxima?: boolean;
}

export type EditarLinhaImportacaoDto = {
  nome?: string;
  ddi?: string;
  numero?: string;
  etiquetas?: string;
}

export type ResponseContatoSincronizacaoDto = {
  id: string;
  idUsuario: string;
  idInbox: string;
  sobreescreverDados: boolean;
  status: string;
  dataCriacao: string;
  dataInicioProcessamento?: string;
  dataTerminoProcessamento?: string;
  linhasJaProcessadas: number;
  totalLinhas?: number;
  inbox?: {
    id: string;
    nome: string;
  };
  usuario?: {
    id: string;
    nome: string;
    foto?: string | null;
  };
}

export class ContatosService extends GenericService<CreateContato, UpdateContato, ResponseContato> {
  constructor() { super('contatos') }

  async getAll(): Promise<ResponseContato[]> {
    // REMOVIDO O FILTRO DE PPS.WHATSAPP.NET
    return await super.getAll();
  }

  async getByConta(idConta: string) {
    const contatos = await this.f.get('', { idConta }) as ResponseContato[];
    // REMOVIDO O FILTRO DE PPS.WHATSAPP.NET
    return contatos;
  }

  /**
   * `idEtiquetas` é filtro OU: o contato entra se tiver ao menos uma delas.
   * Vai como lista separada por vírgula (o backend aceita as duas formas).
   */
  async getByContaPaginated(idConta: string, pagina: number, resultados: number = 100, buscaTextual?: string, idEtiqueta?: string, idEtiquetas?: string[]): Promise<{ dados: ResponseContato[], total: number, pagina: number, resultados: number }> {
    return await this.f.get('', { idConta, pagina, resultados, ...(buscaTextual ? { buscaTextual } : {}), ...(idEtiqueta ? { idEtiqueta } : {}), ...(idEtiquetas?.length ? { idEtiquetas: idEtiquetas.join(',') } : {}) }) as { dados: ResponseContato[], total: number, pagina: number, resultados: number };
  }

  // Busca EXATA por código (número WhatsApp normalizado). Retorna null em 404.
  async getByCodigo(idConta: string, codigo: string): Promise<ResponseContato | null> {
    return await this.f.get('/by-codigo', { idConta, codigo }, { resolve404AsValue: true, resolve404As: null }) as ResponseContato | null;
  }


  async getById(id: string): Promise<ResponseContato | null> {
    const c = await super.getById(id);
    // REMOVIDO O FILTRO DE PPS.WHATSAPP.NET
    return c;
  }

  async sincronizar(data: SincronizarContatosDto) {
    return await this.f.post('/sincronizar', data);
  }

  async uploadFoto(id: string, file: File, codigo: string): Promise<{ url: string }> {
    const storageService = new StorageService();
    // 1. Faz upload via StorageService
    const storageResponse = await storageService.uploadContatoFile({
      contatoId: id,
      codigo: codigo,
      file: file
    });

    // 2. Vincula o arquivo ao contato
    return (await this.f.post(`/${id}/foto`, {
      storageResponse
    })) as { url: string };
  }

  async atualizarPermissaoSincronizacao(
    id: string,
    permitir: boolean,
  ): Promise<{ message: string }> {
    return (await this.f.patch(`/${id}/sincronizacao-foto`, { permitir })) as {
      message: string;
    };
  }

  async importar(data: ImportarContatosDto) {
    return await this.f.post('/importar', data, { formdata: true, jsonPayload: false });
  }

  async enviarImportacao(data: { idConta: string; file: File }): Promise<{ idImportacao: string }> {
    return await this.f.post('/importacoes', data, { formdata: true, jsonPayload: false }) as { idImportacao: string };
  }

  async listarLinhasImportacao(idImportacao: string, query: PaginacaoImportacaoDto): Promise<PaginaImportacaoDto<LinhaConferenciaDto>> {
    // Sinal próprio = sem o dedupe de GET em voo: depois de uma edição a lista precisa ser
    // lida de novo, não reaproveitar uma leitura que começou antes do commit.
    return await this.f.get(`/importacoes/${idImportacao}/linhas`, query, { abtSignal: new AbortController().signal }) as PaginaImportacaoDto<LinhaConferenciaDto>;
  }

  async editarLinhaImportacao(idImportacao: string, idLinha: string, data: EditarLinhaImportacaoDto): Promise<{ linha: LinhaConferenciaDto; indicadores: IndicadoresImportacaoDto }> {
    return await this.f.patch(`/importacoes/${idImportacao}/linhas/${idLinha}`, data) as { linha: LinhaConferenciaDto; indicadores: IndicadoresImportacaoDto };
  }

  async aplicarSugestoesImportacao(idImportacao: string): Promise<{ aplicadas: number; indicadores: IndicadoresImportacaoDto }> {
    return await this.f.post(`/importacoes/${idImportacao}/linhas/aplicar-sugestoes`, {}) as { aplicadas: number; indicadores: IndicadoresImportacaoDto };
  }

  async confirmarImportacao(idImportacao: string, sobreescreverDados: boolean): Promise<{ idImportacao: string }> {
    return await this.f.post(`/importacoes/${idImportacao}/confirmar`, { sobreescreverDados }) as { idImportacao: string };
  }

  async descartarImportacao(idImportacao: string): Promise<{ idImportacao: string }> {
    return await this.f.delete(`/importacoes/${idImportacao}`) as { idImportacao: string };
  }

  async reanalisarImportacao(idImportacao: string): Promise<{ idImportacao: string }> {
    return await this.f.post(`/importacoes/${idImportacao}/reanalisar`, {}) as { idImportacao: string };
  }

  async resultadoImportacao(idImportacao: string, query: PaginacaoImportacaoDto): Promise<PaginaImportacaoDto<ResultadoLinhaDto> & { falhaGeral: string | null }> {
    // Sem o dedupe de GET em voo: ao vivo, a recarga do fim da importação não pode
    // reaproveitar uma leitura que começou antes do último lote.
    return await this.f.get(`/importacoes/${idImportacao}/resultado`, query, { abtSignal: new AbortController().signal }) as PaginaImportacaoDto<ResultadoLinhaDto> & { falhaGeral: string | null };
  }

  async listarImportacoes(data: FiltroContatoImportacaoDto): Promise<ResponseContatoImportacaoDto[]> {
    return await this.f.get('/importacoes', data) as ResponseContatoImportacaoDto[];
  }

  async listarLogs(idImportacao: string) {
    return await this.f.get(`/importacoes/${idImportacao}/logs`);
  }

  async listarSincronizacoes(data: FiltroContatoSincronizacaoDto): Promise<ResponseContatoSincronizacaoDto[]> {
    return await this.f.get('/sincronizacoes', data) as ResponseContatoSincronizacaoDto[];
  }

  async atribuirAtendenteFixo(idContato: IdContato, idAtendente: IdUsuario, idInbox: IdInbox): Promise<ResponseUsuario> {
    return await this.f.post(`/${idContato}/atendentes-fixos`, { idInbox, idAtendente }) as ResponseUsuario;
  }

  async buscarAtendenteFixo(idContato: IdContato, idInbox: IdInbox): Promise<ResponseUsuario | null> {
    const response = await this.f.get(`/${idContato}/atendentes-fixos`, { idInbox });

    if (!response) return null;

    return response as ResponseUsuario;
  }

  async removerAtendenteFixo(idContato: IdContato, idInbox: IdInbox) {
    return await this.f.delete(`/${idContato}/atendentes-fixos`, { idInbox });
  }

  async limparContatos015(idConta: string) {
    return await this.f.patch('/limpar-contatos-015', { idConta });
  }

  async getSemAtendimentoAberto(idConta: string, idInbox: string, opts?: { limit?: number; offset?: number; busca?: string }): Promise<{ items: ContatoSemAtendimento[]; hasMore: boolean }> {
      const params: Record<string, string | number> = { idConta, idInbox };
      if (opts?.limit != null) params.limit = opts.limit;
      if (opts?.offset != null) params.offset = opts.offset;
      if (opts?.busca) params.busca = opts.busca;

      const res = await this.f.get(`/sem-atendimento-aberto/paginado`, params, { resolve404AsValue: true, resolve404As: null });
      if (res != null) return res as { items: ContatoSemAtendimento[]; hasMore: boolean };

      // Compat: backend antigo não tem a rota paginada → rota legada (lista completa, sem paginação)
      const legado = await this.f.get(`/sem-atendimento-aberto`, { idConta, idInbox }) as ContatoSemAtendimento[];
      return { items: legado, hasMore: false };
  }

  async verificarExistenciaWpp(idContato: string) {
    return await this.f.get(`/${idContato}/verificar-existencia-wpp`) as { existe: boolean | undefined };
  }

  /** `motivo` preenchido = a busca NÃO aconteceu (canal sem foto de contato). */
  async buscarFotoPerfil(codigo: string, idConta: string): Promise<RespostaFotoDeContato> {
    return (await this.f.post('/buscar-foto-perfil', { codigo, idConta })) as RespostaFotoDeContato;
  }
}
